import { useChartStore } from '@/store/chartStore';
import type { CorporateEvent, CorporateEventType } from '@/types';
import { cancelTooltipClear, scheduleTooltipClear } from './eventTooltipState';

const TYPE_LABEL: Record<CorporateEventType, string> = {
  div:               'Cash Dividend',
  div_script:        'Scrip Dividend',
  div_ann:           'Dividend Announced',
  rights:            'Rights Issue',
  rights_ann:        'Rights Announced',
  split:             'Share Split',
  split_ann:         'Split Announced',
  consolidation:     'Consolidation',
  consolidation_ann: 'Consolidation Announced',
  report:            'Financial Report',
  agm:               'Annual General Meeting',
  egm:               'Extraordinary GM',
  halt:              'Trading Halt',
};

const TYPE_COLOR: Record<CorporateEventType, string> = {
  div:               '#26A69A',
  div_script:        '#80CBC4',
  div_ann:           '#4DB6AC',
  rights:            '#42A5F5',
  rights_ann:        '#90CAF9',
  split:             '#AB47BC',
  split_ann:         '#CE93D8',
  consolidation:     '#EF5350',
  consolidation_ann: '#EF9A9A',
  report:            '#FFA726',
  agm:               '#78909C',
  egm:               '#90A4AE',
  halt:              '#B71C1C',
};

function EventCard({ ev }: { ev: CorporateEvent }) {
  const color = TYPE_COLOR[ev.type];
  const label = TYPE_LABEL[ev.type];

  return (
    <div className="mb-2 last:mb-0">
      {/* Type badge */}
      <div className="flex items-center gap-1.5 mb-0.5">
        <span
          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color }}>
          {label}
        </span>
      </div>

      {/* Title */}
      <p className="text-xs font-medium text-white leading-tight mb-0.5">{ev.title}</p>

      {/* Detail */}
      {ev.detail && (
        <p className="text-[11px] text-gray-300 leading-snug mb-0.5">{ev.detail}</p>
      )}

      {/* Dates */}
      <div className="text-[10px] text-gray-400 space-y-px">
        {ev.announcedDate && (
          <div>Announced: <span className="text-gray-300">{ev.announcedDate}</span></div>
        )}
        {ev.xdDate && ev.xdDate !== ev.announcedDate && (
          <div>XD/XR/Ex: <span className="text-gray-300">{ev.xdDate}</span></div>
        )}
        {ev.ratio && (
          <div>Ratio: <span className="text-gray-300">{ev.ratio}</span></div>
        )}
        {ev.amount != null && ev.type !== 'split' && ev.type !== 'consolidation' && (
          <div>
            {ev.type.startsWith('rights') ? 'Issue price' : 'Amount'}:&nbsp;
            <span className="text-gray-300">LKR {ev.amount.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* PDF link */}
      {ev.pdfUrl && (
        <a
          href={ev.pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-1 text-[10px] text-blue-400 hover:text-blue-300 underline underline-offset-2"
        >
          View Announcement PDF ↗
        </a>
      )}
    </div>
  );
}

export function EventTooltip() {
  const hoveredEvents   = useChartStore((s) => s.hoveredEvents);
  const hoveredEventBar = useChartStore((s) => s.hoveredEventBar);
  const pinnedEvents    = useChartStore((s) => s.pinnedEvents);
  const pinnedEventBar  = useChartStore((s) => s.pinnedEventBar);
  const setPinnedEvents = useChartStore((s) => s.setPinnedEvents);

  // Pinned takes priority over hovered
  const events = pinnedEvents ?? hoveredEvents;
  const pos    = pinnedEventBar ?? hoveredEventBar;
  const pinned = pinnedEvents !== null;

  if (!events || !pos || events.length === 0) return null;

  const TOOLTIP_W = 260;
  const left   = Math.max(8, pos.x - TOOLTIP_W / 2);
  const bottom = `calc(100% - ${pos.y - 18}px)`;

  return (
    <div
      data-chart-tooltip
      className="absolute z-dropdown pointer-events-auto select-text"
      style={{ left, bottom, width: TOOLTIP_W, maxWidth: 'calc(100% - 16px)' }}
      onMouseEnter={cancelTooltipClear}
      onMouseLeave={() => { if (!pinned) scheduleTooltipClear(); }}
      onMouseMove={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-2.5 text-left">
        {/* Close button when pinned */}
        {pinned && (
          <button
            className="absolute top-1.5 right-1.5 text-gray-500 hover:text-white leading-none text-base"
            onClick={() => setPinnedEvents(null, null)}
            aria-label="Close"
          >
            ×
          </button>
        )}
        {events.map((ev, idx) => (
          <EventCard key={`${ev.type}-${ev.date}-${idx}`} ev={ev} />
        ))}
      </div>
    </div>
  );
}
